import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DollarSign, TrendingUp, CreditCard, Calendar, Clock, Play, Square, Plus, ChevronLeft, ChevronRight, Eye, Percent, Settings, Trash2, Receipt, Building, Wrench } from 'lucide-react';
import api, { propertyAPI, roomAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METODO_LABELS = {
  zelle: 'Zelle', pago_movil: 'Pago Móvil', efectivo: 'Efectivo',
  tarjeta: 'Tarjeta', transferencia: 'Transferencia', paypal: 'PayPal', otro: 'Otro'
};

const fmt = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function InformesPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isSubUser = user?.role === 'propietario' || user?.role === 'limitado';

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  // Shifts
  const [shifts, setShifts] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [shiftDetailModal, setShiftDetailModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [expenseModal, setExpenseModal] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseConcept, setExpenseConcept] = useState('');
  const [adminCommission, setAdminCommission] = useState(0);
  const [editingCommission, setEditingCommission] = useState(false);
  const [creditStats, setCreditStats] = useState({ total_por_cobrar: 0, total_creditos: 0 });
  const [expenseGeneralModal, setExpenseGeneralModal] = useState(false);
  const [newExpense, setNewExpense] = useState({ concepto: '', monto: '', categoria: 'general', fecha: '', alcance: 'propiedad', room_id: '' });
  const [otaCommissions, setOtaCommissions] = useState({});
  const [editingOta, setEditingOta] = useState(null);
  const [savingOta, setSavingOta] = useState(false);
  const [incomeModal, setIncomeModal] = useState(false);
  const [newIncome, setNewIncome] = useState({ motivo: '', monto: '', categoria: 'otro', fecha: '', alcance: 'propiedad', room_id: '' });

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      const props = res.data;
      setProperties(props);
      
    });
  }, []);

  // Load rooms when property changes
  useEffect(() => {
    if (!selectedProperty) return;
    setSelectedRoom('all');
    roomAPI.getAll(selectedProperty).then(res => {
      setRooms(res.data);
    }).catch(() => setRooms([]));
  }, [selectedProperty]);

  const fetchReport = useCallback(async () => {
    if (!selectedProperty) return;
    setLoading(true);
    try {
      const params = [];
      if (rangeStart) params.push(`fecha_inicio=${rangeStart}`);
      if (rangeEnd) params.push(`fecha_fin=${rangeEnd}`);
      if (selectedRoom && selectedRoom !== 'all') params.push(`room_id=${selectedRoom}`);
      const res = await api.get(`/reports/${selectedProperty}${params.length ? '?' + params.join('&') : ''}`);
      setReportData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [selectedProperty, rangeStart, rangeEnd, selectedRoom]);

  const fetchShifts = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await api.get(`/shifts/${selectedProperty}/list`);
      setShifts(res.data);
      setActiveShift(res.data.find(s => s.status === 'open') || null);
    } catch (err) { console.error(err); }
  }, [selectedProperty]);

  useEffect(() => { fetchReport(); fetchShifts(); }, [fetchReport, fetchShifts]);

  // Fetch admin commission
  useEffect(() => {
    if (!selectedProperty) return;
    const token = localStorage.getItem('token');
    axios.get(`${BACKEND_URL}/api/admin-commission/${selectedProperty}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setAdminCommission(res.data.porcentaje || 0)).catch(() => {});
    // Fetch OTA commission settings
    axios.get(`${BACKEND_URL}/api/ota-commissions/${selectedProperty}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setOtaCommissions(res.data)).catch(() => {});
    // Fetch credit/accounts receivable stats
    axios.get(`${BACKEND_URL}/api/credits/stats?property_id=${selectedProperty}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setCreditStats(res.data)).catch(() => {});
  }, [selectedProperty]);

  const saveAdminCommission = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin-commission/${selectedProperty}`, {
        porcentaje: adminCommission
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Comisión administrativa: ${adminCommission}%`);
      setEditingCommission(false);
      fetchReport();
    } catch (e) { toast.error('Error al guardar comisión'); }
  };

  const addGeneralExpense = async () => {
    if (!newExpense.concepto || !newExpense.monto) { toast.error('Concepto y monto son obligatorios'); return; }
    if (newExpense.alcance === 'habitacion' && !newExpense.room_id) { toast.error('Selecciona una habitación'); return; }
    try {
      const token = localStorage.getItem('token');
      const selectedRoomObj = rooms.find(r => r.id === newExpense.room_id);
      await axios.post(`${BACKEND_URL}/api/property-expenses/${selectedProperty}`, {
        concepto: newExpense.concepto,
        monto: parseFloat(newExpense.monto),
        categoria: newExpense.categoria,
        fecha: newExpense.fecha || new Date().toISOString().slice(0, 10),
        alcance: newExpense.alcance,
        room_id: newExpense.alcance === 'habitacion' ? newExpense.room_id : null,
        room_name: newExpense.alcance === 'habitacion' ? (selectedRoomObj?.nombre || '') : ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Gasto registrado');
      setExpenseGeneralModal(false);
      setNewExpense({ concepto: '', monto: '', categoria: 'general', fecha: '', alcance: 'propiedad', room_id: '' });
      fetchReport();
    } catch (e) { toast.error('Error al registrar gasto'); }
  };

  const deleteGeneralExpense = async (expenseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/property-expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gasto eliminado');
      fetchReport();
    } catch (e) { toast.error('Error al eliminar gasto'); }
  };

  const addAdditionalIncome = async () => {
    if (!newIncome.motivo || !newIncome.monto) { toast.error('Motivo y monto son obligatorios'); return; }
    if (newIncome.alcance === 'habitacion' && !newIncome.room_id) { toast.error('Selecciona una habitación'); return; }
    try {
      const token = localStorage.getItem('token');
      const selectedRoomObj = rooms.find(r => r.id === newIncome.room_id);
      await axios.post(`${BACKEND_URL}/api/additional-income/${selectedProperty}`, {
        motivo: newIncome.motivo,
        monto: parseFloat(newIncome.monto),
        categoria: newIncome.categoria,
        fecha: newIncome.fecha || new Date().toISOString().slice(0, 10),
        alcance: newIncome.alcance,
        room_id: newIncome.alcance === 'habitacion' ? newIncome.room_id : null,
        room_name: newIncome.alcance === 'habitacion' ? (selectedRoomObj?.nombre || '') : ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ingreso adicional registrado');
      setIncomeModal(false);
      setNewIncome({ motivo: '', monto: '', categoria: 'otro', fecha: '', alcance: 'propiedad', room_id: '' });
      fetchReport();
    } catch (e) { toast.error('Error al registrar ingreso'); }
  };

  const deleteAdditionalIncome = async (incomeId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/additional-income/${incomeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ingreso eliminado');
      fetchReport();
    } catch (e) { toast.error('Error al eliminar ingreso'); }
  };

  const saveOtaCommissions = async () => {
    setSavingOta(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/ota-commissions`, {
        property_id: selectedProperty,
        ...otaCommissions
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Comisiones OTA guardadas');
      setEditingOta(null);
      fetchReport();
    } catch (e) { toast.error('Error al guardar comisiones'); }
    finally { setSavingOta(false); }
  };

  // Calendar helpers
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  const toDateStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const handleDayClick = (day) => {
    const dateStr = toDateStr(day);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(dateStr);
      setRangeEnd(null);
    } else {
      if (dateStr < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(dateStr);
      } else {
        setRangeEnd(dateStr);
      }
    }
  };

  const isInRange = (day) => {
    const d = toDateStr(day);
    if (rangeStart && rangeEnd) return d >= rangeStart && d <= rangeEnd;
    if (rangeStart) return d === rangeStart;
    return false;
  };

  const clearRange = () => { setRangeStart(null); setRangeEnd(null); };
  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Shift actions
  const openShift = async () => {
    try {
      await api.post('/shifts/open', { property_id: selectedProperty });
      toast.success('Turno abierto');
      fetchShifts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const closeShift = async () => {
    if (!activeShift) return;
    try {
      const res = await api.post(`/shifts/${activeShift.id}/close`);
      toast.success(`Turno cerrado. Ingresos: ${fmt(res.data.total_ingresos)} | Gastos: ${fmt(res.data.total_gastos)}`);
      fetchShifts();
      fetchReport();
    } catch (err) { toast.error('Error cerrando turno'); }
  };

  const addExpense = async () => {
    if (!activeShift || !expenseAmount) return;
    try {
      await api.post(`/shifts/${activeShift.id}/expense`, {
        monto: parseFloat(expenseAmount),
        concepto: expenseConcept
      });
      toast.success('Gasto registrado');
      setExpenseModal(false);
      setExpenseAmount('');
      setExpenseConcept('');
      fetchShifts();
    } catch (err) { toast.error('Error'); }
  };

  const viewShiftDetail = async (shift) => {
    try {
      const res = await api.get(`/shifts/${shift.id}/detail`);
      setSelectedShift(res.data);
      setShiftDetailModal(true);
    } catch (err) { toast.error('Error'); }
  };

  const dayPayments = reportData?.por_dia || {};
  const methodEntries = reportData?.por_metodo ? Object.entries(reportData.por_metodo) : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="informes-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Informes</h1>
            <p className="text-muted-foreground mt-1">Sumatoria general de ingresos y gastos por turnos</p>
          </div>
          {properties.length > 0 && (
            <div className="flex items-center gap-3">
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[220px]" data-testid="informes-property-selector">
                  <SelectValue placeholder="Selecciona propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              {rooms.length > 0 && (
                <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                  <SelectTrigger className="w-[220px]" data-testid="informes-room-selector">
                    <SelectValue placeholder="Todas las habitaciones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las habitaciones</SelectItem>
                    {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Shift Control */}
        <Card data-testid="shift-control">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {activeShift ? (
                      <span className="text-green-600">Turno abierto desde {new Date(activeShift.opened_at).toLocaleString('es-CO')}</span>
                    ) : (
                      <span className="text-muted-foreground">Sin turno activo</span>
                    )}
                  </p>
                  {activeShift && (
                    <p className="text-xs text-muted-foreground">por {activeShift.user_name}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {activeShift ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setExpenseModal(true)} data-testid="btn-add-expense">
                      <Plus className="h-4 w-4 mr-1" /> Registrar Gasto
                    </Button>
                    <Button variant="destructive" size="sm" onClick={closeShift} data-testid="btn-close-shift">
                      <Square className="h-4 w-4 mr-1" /> Cerrar Turno
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={openShift} data-testid="btn-open-shift">
                    <Play className="h-4 w-4 mr-1" /> Abrir Turno
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-1" data-testid="report-calendar">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <button onClick={prevMonth} className="p-1 hover:bg-muted rounded"><ChevronLeft className="h-4 w-4" /></button>
                <CardTitle className="text-sm capitalize">{monthName}</CardTitle>
                <button onClick={nextMonth} className="p-1 hover:bg-muted rounded"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1">
                {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'].map(d => (
                  <div key={d} className="text-muted-foreground font-medium py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const dateStr = toDateStr(day);
                  const inRange = isInRange(day);
                  const isStart = dateStr === rangeStart;
                  const isEnd = dateStr === rangeEnd;
                  const hasPayment = dayPayments[dateStr] > 0;
                  return (
                    <button
                      key={day}
                      onClick={() => handleDayClick(day)}
                      className={`relative p-1.5 text-xs rounded-md transition-all
                        ${inRange ? 'bg-primary text-white' : 'hover:bg-muted'}
                        ${(isStart || isEnd) ? 'ring-2 ring-primary ring-offset-1' : ''}
                      `}
                    >
                      {day}
                      {hasPayment && !inRange && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-green-500 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
              {(rangeStart || rangeEnd) && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    {rangeStart && <span>Desde: <strong>{rangeStart}</strong></span>}
                    {rangeEnd && <span> → Hasta: <strong>{rangeEnd}</strong></span>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearRange} className="text-xs w-full">Limpiar filtro</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Cards + Method Breakdown */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Cargando...</div>
            ) : reportData ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card className="bg-green-50 border-green-200" data-testid="card-ingresos">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[10px] font-medium text-green-600">Ingresos Reales</p>
                      <p className="text-xl font-bold text-green-700">{fmt(reportData.total_ingresos_reales)}</p>
                      <p className="text-[10px] text-green-500">{reportData.total_pagos} pagos</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200" data-testid="card-reservas">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[10px] font-medium text-blue-600">Valor Reservas</p>
                      <p className="text-xl font-bold text-blue-700">{fmt(reportData.total_valor_reservas)}</p>
                      <p className="text-[10px] text-blue-500">{reportData.total_reservas} reservas</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-yellow-50 border-yellow-200" data-testid="card-cuentas-cobrar">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[10px] font-medium text-yellow-600">Cuentas por Cobrar</p>
                      <p className="text-xl font-bold text-yellow-700">{fmt(creditStats.total_por_cobrar)}</p>
                      <p className="text-[10px] text-yellow-500">{creditStats.total_creditos || 0} créditos</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200" data-testid="card-cobro">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-[10px] font-medium text-purple-600">Tasa de Cobro</p>
                      <p className="text-xl font-bold text-purple-700">
                        {reportData.total_valor_reservas > 0 ? Math.round((reportData.total_ingresos_reales / reportData.total_valor_reservas) * 100) : 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Method Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm" style={{ fontFamily: 'Manrope' }}>Por Método de Pago</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {methodEntries.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Sin pagos</p>
                    ) : (
                      <div className="space-y-2">
                        {methodEntries.sort((a, b) => b[1].total - a[1].total).map(([key, val]) => (
                          <div key={key}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{METODO_LABELS[key] || val.nombre}</span>
                              <span className="text-muted-foreground">{fmt(val.total)} ({val.count})</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className="bg-primary h-2 rounded-full" style={{ width: `${reportData.total_ingresos_reales > 0 ? (val.total / reportData.total_ingresos_reales * 100) : 0}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Desglose por Canal / OTA */}
                <Card data-testid="channel-breakdown">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Building className="h-4 w-4" />
                      Ingresos por Canal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(() => {
                      const canales = reportData.por_canal || {};
                      const OTA_CONFIG = [
                        { key: 'Booking', label: 'Booking.com', color: 'blue', fieldKey: 'booking_commission' },
                        { key: 'Airbnb', label: 'Airbnb', color: 'rose', fieldKey: 'airbnb_commission' },
                        { key: 'Expedia', label: 'Expedia', color: 'amber', fieldKey: 'expedia_commission' },
                        { key: 'Priced Travel', label: 'Priced Travel', color: 'teal', fieldKey: 'priced_travel_commission' },
                      ];
                      return (
                        <>
                          {/* OTA Cards */}
                          <div className="grid md:grid-cols-2 gap-3">
                            {OTA_CONFIG.map(ota => {
                              const ch = canales[ota.key] || { reservas: 0, nacionales: 0, extranjeros: 0, ingreso_bruto: 0, comision: 0, iva_comision: 0, neto: 0, comision_pct: 0 };
                              const isEditing = editingOta === ota.key;
                              return (
                                <div key={ota.key} className={`border rounded-lg overflow-hidden`} data-testid={`canal-${ota.key.toLowerCase().replace(' ', '-')}`}>
                                  <div className={`flex items-center justify-between px-3 py-2 bg-${ota.color}-50 border-b border-${ota.color}-100`}>
                                    <span className={`font-semibold text-sm text-${ota.color}-700`}>{ota.label}</span>
                                    <button onClick={() => setEditingOta(isEditing ? null : ota.key)} className={`text-xs text-${ota.color}-500 hover:underline flex items-center gap-1`} data-testid={`config-${ota.key.toLowerCase().replace(' ', '-')}`}>
                                      <Settings className="h-3 w-3" /> Config
                                    </button>
                                  </div>
                                  {isEditing && (
                                    <div className="px-3 py-2 bg-gray-50 border-b space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs w-24">Comisión %</Label>
                                        <Input type="number" min="0" max="100" step="0.1" className="h-7 w-20 text-xs" value={otaCommissions[ota.fieldKey] || ''} onChange={e => setOtaCommissions({ ...otaCommissions, [ota.fieldKey]: parseFloat(e.target.value) || 0 })} data-testid={`ota-com-${ota.fieldKey}`} />
                                        <Button size="sm" className="h-7 text-xs" onClick={saveOtaCommissions} disabled={savingOta}>Guardar</Button>
                                      </div>
                                    </div>
                                  )}
                                  <div className="px-3 py-2 space-y-1.5">
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                      <span>{ch.reservas} reserva{ch.reservas !== 1 ? 's' : ''}</span>
                                      <span>{ch.nacionales}N / {ch.extranjeros}E</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">Ingreso bruto</span>
                                      <span className="font-medium">{fmt(ch.ingreso_bruto)}</span>
                                    </div>
                                    {ch.ingreso_bruto > 0 && (
                                      <>
                                        <div className="flex justify-between text-xs text-red-500">
                                          <span>(-) Comisión {ch.comision_pct}%</span>
                                          <span>-{fmt(ch.comision)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-amber-500">
                                          <span>(-) IVA 19% s/comisión</span>
                                          <span>-{fmt(ch.iva_comision)}</span>
                                        </div>
                                      </>
                                    )}
                                    <div className={`flex justify-between text-sm font-bold pt-1 border-t`}>
                                      <span>Neto</span>
                                      <span className={`text-${ota.color}-700`}>{fmt(ch.neto)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Ventas Directas */}
                          {(() => {
                            const directo = canales['Directo'] || { reservas: 0, nacionales: 0, extranjeros: 0, ingreso_bruto: 0, neto: 0 };
                            return (
                              <div className="border rounded-lg overflow-hidden" data-testid="canal-directo">
                                <div className="flex items-center justify-between px-3 py-2 bg-green-50 border-b border-green-100">
                                  <span className="font-semibold text-sm text-green-700">Ventas Directas (WhatsApp, Motor, Directo)</span>
                                  <Badge variant="outline" className="text-[10px] border-green-300 text-green-600">Sin comisión</Badge>
                                </div>
                                <div className="px-3 py-2 space-y-1.5">
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{directo.reservas} reserva{directo.reservas !== 1 ? 's' : ''}</span>
                                    <span>{directo.nacionales}N / {directo.extranjeros}E</span>
                                  </div>
                                  <div className="flex justify-between text-sm font-bold">
                                    <span>Neto</span>
                                    <span className="text-green-700">{fmt(directo.neto)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Suma Total */}
                          <div className="bg-slate-800 text-white rounded-lg p-3 flex justify-between items-center" data-testid="total-neto-canales">
                            <span className="font-semibold">Suma Total Ingresos Netos</span>
                            <span className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{fmt(reportData.total_neto_canales || reportData.neto_despues_iva)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Liquidación al Propietario */}
                <Card data-testid="financial-breakdown">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Receipt className="h-4 w-4" />
                        Liquidación al Propietario
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => setEditingCommission(!editingCommission)}
                        data-testid="edit-commission-btn"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        Comisión Admin.
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {/* Total from all channels */}
                    <div className="flex justify-between items-center py-2 bg-slate-50 rounded-lg px-3">
                      <span className="text-sm font-semibold text-slate-700">Total Neto de Ventas (todos los canales)</span>
                      <span className="text-lg font-bold text-slate-800" data-testid="total-ventas">{fmt(reportData.total_neto_canales || reportData.neto_despues_iva)}</span>
                    </div>

                    {/* Admin Commission */}
                    <Separator className="my-1" />
                    
                    {editingCommission && (
                      <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                        <Label className="text-xs whitespace-nowrap font-medium">Comisión %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={adminCommission}
                          onChange={(e) => setAdminCommission(parseFloat(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                          data-testid="admin-commission-input"
                        />
                        <Button size="sm" className="h-8 text-xs bg-slate-800 hover:bg-slate-700" onClick={saveAdminCommission} data-testid="save-commission-btn">Guardar</Button>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-sm py-1.5 px-3 text-purple-600">
                      <span>(-) Comisión Administrador ({reportData.comision_admin_porcentaje}%)</span>
                      <span>-{fmt(reportData.comision_admin_valor)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-1.5 px-3 text-purple-500">
                      <span className="pl-4">(+) IVA 19% sobre comisión</span>
                      <span>-{fmt(reportData.iva_sobre_comision)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-1.5 px-3 bg-purple-50 rounded font-medium text-purple-700">
                      <span>Total cobra el administrador</span>
                      <span data-testid="total-cobra-admin">{fmt(reportData.total_cobra_admin)}</span>
                    </div>

                    <Separator className="my-1" />

                    <div className="flex justify-between text-sm font-semibold py-1.5 px-3">
                      <span>Subtotal después de comisión</span>
                      <span data-testid="subtotal-despues-admin">{fmt(reportData.subtotal_despues_admin)}</span>
                    </div>

                    {/* Shift expenses */}
                    {reportData.total_gastos > 0 && (
                      <div className="flex justify-between text-sm py-1.5 px-3 text-red-500">
                        <span>(-) Gastos de turno</span>
                        <span>-{fmt(reportData.total_gastos)}</span>
                      </div>
                    )}

                    {/* General Expenses */}
                    <div className="border rounded-lg mt-1">
                      <div className="flex items-center justify-between px-3 py-2 bg-red-50/50">
                        <span className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                          <Wrench className="h-3.5 w-3.5" />
                          Gastos Generales del Período
                        </span>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => { setNewExpense({ concepto: '', monto: '', categoria: 'general', fecha: '', alcance: 'propiedad', room_id: '' }); setExpenseGeneralModal(true); }} data-testid="btn-add-general-expense">
                          <Plus className="h-3 w-3 mr-1" /> Agregar Gasto
                        </Button>
                      </div>
                      {reportData.gastos_generales?.length > 0 ? (
                        <div className="divide-y">
                          {reportData.gastos_generales.map(g => (
                            <div key={g.id} className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50" data-testid={`expense-${g.id}`}>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{g.concepto}</span>
                                {g.room_name ? (
                                  <span className="text-[10px] ml-1.5 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{g.room_name}</span>
                                ) : (
                                  <span className="text-[10px] ml-1.5 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">General</span>
                                )}
                                <span className="text-xs text-muted-foreground ml-2">{g.fecha}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-red-600 font-medium">-{fmt(g.monto)}</span>
                                <button onClick={() => deleteGeneralExpense(g.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-expense-${g.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-between px-3 py-2 font-medium text-sm text-red-700 bg-red-50/30">
                            <span>Total Gastos Generales</span>
                            <span>-{fmt(reportData.total_gastos_generales)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center py-3">Sin gastos generales registrados</p>
                      )}
                    </div>

                    {/* Additional Income */}
                    <div className="border rounded-lg mt-1">
                      <div className="flex items-center justify-between px-3 py-2 bg-green-50/50">
                        <span className="text-sm font-medium text-green-700 flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5" />
                          Ingresos Adicionales
                        </span>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-green-300 text-green-600 hover:bg-green-50" onClick={() => { setNewIncome({ motivo: '', monto: '', categoria: 'otro', fecha: '', alcance: 'propiedad', room_id: '' }); setIncomeModal(true); }} data-testid="btn-add-additional-income">
                          <Plus className="h-3 w-3 mr-1" /> Agregar Ingreso
                        </Button>
                      </div>
                      {reportData.ingresos_adicionales?.length > 0 ? (
                        <div className="divide-y">
                          {reportData.ingresos_adicionales.map(inc => (
                            <div key={inc.id} className="flex items-center justify-between px-3 py-1.5 text-sm hover:bg-gray-50" data-testid={`income-${inc.id}`}>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{inc.motivo}</span>
                                {inc.room_name ? (
                                  <span className="text-[10px] ml-1.5 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{inc.room_name}</span>
                                ) : (
                                  <span className="text-[10px] ml-1.5 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">General</span>
                                )}
                                <span className="text-xs text-muted-foreground ml-2">{inc.fecha}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-green-600 font-medium">+{fmt(inc.monto)}</span>
                                <button onClick={() => deleteAdditionalIncome(inc.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-income-${inc.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-between px-3 py-2 font-medium text-sm text-green-700 bg-green-50/30">
                            <span>Total Ingresos Adicionales</span>
                            <span>+{fmt(reportData.total_ingresos_adicionales)}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center py-3">Sin ingresos adicionales registrados</p>
                      )}
                    </div>

                    {/* FINAL: Owner's Profit */}
                    <Separator className="border-2 my-2" />
                    <div className={`flex justify-between items-center text-lg font-bold rounded-lg p-3 ${reportData.total_neto_final >= 0 ? 'bg-green-50' : 'bg-red-50'}`} data-testid="ganancia-propietario">
                      <span className={reportData.total_neto_final >= 0 ? 'text-green-800' : 'text-red-800'}>
                        GANANCIA DEL PROPIETARIO
                      </span>
                      <span className={reportData.total_neto_final >= 0 ? 'text-green-700' : 'text-red-700'} style={{ fontFamily: 'Manrope' }}>
                        {fmt(reportData.total_neto_final)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </div>
        </div>

        {/* Shifts History */}
        <Card data-testid="shifts-history">
          <CardHeader>
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Historial de Turnos</CardTitle>
          </CardHeader>
          <CardContent>
            {shifts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No hay turnos registrados</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Apertura</th>
                      <th className="text-left py-2 px-2">Cierre</th>
                      <th className="text-left py-2 px-2">Operador</th>
                      <th className="text-right py-2 px-2">Ingresos</th>
                      <th className="text-right py-2 px-2">Gastos</th>
                      <th className="text-right py-2 px-2">Neto</th>
                      <th className="text-center py-2 px-2">Estado</th>
                      <th className="text-center py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map(s => (
                      <tr key={s.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 px-2 text-xs">{new Date(s.opened_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td className="py-2 px-2 text-xs">{s.closed_at ? new Date(s.closed_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                        <td className="py-2 px-2">{s.user_name}</td>
                        <td className="py-2 px-2 text-right text-green-600 font-medium">{fmt(s.total_ingresos)}</td>
                        <td className="py-2 px-2 text-right text-red-500 font-medium">{fmt(s.total_gastos)}</td>
                        <td className="py-2 px-2 text-right font-bold">{fmt((s.total_ingresos || 0) - (s.total_gastos || 0))}</td>
                        <td className="py-2 px-2 text-center">
                          <Badge variant={s.status === 'open' ? 'default' : 'secondary'}>
                            {s.status === 'open' ? 'Abierto' : 'Cerrado'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Button variant="ghost" size="sm" onClick={() => viewShiftDetail(s)} data-testid={`view-shift-${s.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shift Detail Modal */}
        <Dialog open={shiftDetailModal} onOpenChange={setShiftDetailModal}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>Detalle del Turno</DialogTitle>
            </DialogHeader>
            {selectedShift && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600">Ingresos</p>
                    <p className="text-lg font-bold text-green-700">{fmt(selectedShift.total_ingresos)}</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600">Gastos</p>
                    <p className="text-lg font-bold text-red-700">{fmt(selectedShift.total_gastos)}</p>
                  </div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Neto del Turno</p>
                  <p className="text-xl font-bold">{fmt(selectedShift.total_ingresos - selectedShift.total_gastos)}</p>
                </div>

                <Separator />
                <p className="text-sm font-semibold">Pagos recibidos ({selectedShift.pagos?.length || 0})</p>
                {selectedShift.pagos?.length > 0 ? (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedShift.pagos.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b">
                        <span>{METODO_LABELS[p.metodo_pago] || p.metodo_pago}</span>
                        <span className="font-medium text-green-600">{fmt(p.monto)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">Sin pagos</p>}

                <Separator />
                <p className="text-sm font-semibold">Gastos registrados ({selectedShift.gastos?.length || 0})</p>
                {selectedShift.gastos?.length > 0 ? (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedShift.gastos.map((g, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b">
                        <span>{g.concepto || 'Sin concepto'}</span>
                        <span className="font-medium text-red-500">{fmt(g.monto)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">Sin gastos</p>}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Expense Modal (Shift) */}
        <Dialog open={expenseModal} onOpenChange={setExpenseModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Gasto de Turno</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Monto</Label>
                <Input type="number" placeholder="0" value={expenseAmount} onChange={e => setExpenseAmount(e.target.value)} data-testid="expense-amount" />
              </div>
              <div>
                <Label>Concepto</Label>
                <Input placeholder="Ej: Compra de insumos" value={expenseConcept} onChange={e => setExpenseConcept(e.target.value)} data-testid="expense-concept" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExpenseModal(false)}>Cancelar</Button>
              <Button onClick={addExpense} data-testid="btn-save-expense">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* General Expense Modal */}
        <Dialog open={expenseGeneralModal} onOpenChange={setExpenseGeneralModal}>
          <DialogContent className="max-w-md" data-testid="general-expense-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Wrench className="h-5 w-5 text-red-600" />
                Registrar Gasto
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Alcance: General o por Habitación */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Aplicar gasto a</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setNewExpense({ ...newExpense, alcance: 'propiedad', room_id: '' })}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${newExpense.alcance === 'propiedad' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid="expense-alcance-propiedad">
                    Toda la propiedad
                  </button>
                  <button type="button" onClick={() => setNewExpense({ ...newExpense, alcance: 'habitacion' })}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${newExpense.alcance === 'habitacion' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid="expense-alcance-habitacion">
                    Una habitación
                  </button>
                </div>
              </div>

              {/* Room selector - only when alcance = habitacion */}
              {newExpense.alcance === 'habitacion' && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Habitación / Apartamento *</Label>
                  <Select value={newExpense.room_id} onValueChange={v => setNewExpense({ ...newExpense, room_id: v })}>
                    <SelectTrigger data-testid="expense-room-select"><SelectValue placeholder="Seleccionar habitación..." /></SelectTrigger>
                    <SelectContent>
                      {rooms.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nombre} {r.tipo ? `(${r.tipo})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm">Concepto *</Label>
                <Input placeholder="Ej: Pago de servicios públicos" value={newExpense.concepto} onChange={e => setNewExpense({ ...newExpense, concepto: e.target.value })} data-testid="general-expense-concepto" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Monto *</Label>
                  <Input type="number" min="0" placeholder="0" value={newExpense.monto} onChange={e => setNewExpense({ ...newExpense, monto: e.target.value })} data-testid="general-expense-monto" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Fecha</Label>
                  <Input type="date" value={newExpense.fecha} onChange={e => setNewExpense({ ...newExpense, fecha: e.target.value })} data-testid="general-expense-fecha" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Categoría</Label>
                <Select value={newExpense.categoria} onValueChange={v => setNewExpense({ ...newExpense, categoria: v })}>
                  <SelectTrigger data-testid="general-expense-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="servicios">Servicios Públicos</SelectItem>
                    <SelectItem value="lavanderia">Lavandería</SelectItem>
                    <SelectItem value="reparaciones">Reparaciones / Daños</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                    <SelectItem value="insumos">Insumos / Suministros</SelectItem>
                    <SelectItem value="nomina">Nómina / Personal</SelectItem>
                    <SelectItem value="general">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExpenseGeneralModal(false)}>Cancelar</Button>
              <Button onClick={addGeneralExpense} className="bg-slate-800 hover:bg-slate-700" disabled={!newExpense.concepto || !newExpense.monto || (newExpense.alcance === 'habitacion' && !newExpense.room_id)} data-testid="btn-save-general-expense">
                Registrar Gasto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Additional Income Modal */}
        <Dialog open={incomeModal} onOpenChange={setIncomeModal}>
          <DialogContent className="max-w-md" data-testid="additional-income-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <DollarSign className="h-5 w-5 text-green-600" />
                Registrar Ingreso Adicional
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Aplicar ingreso a</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setNewIncome({ ...newIncome, alcance: 'propiedad', room_id: '' })}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${newIncome.alcance === 'propiedad' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid="income-alcance-propiedad">
                    Toda la propiedad
                  </button>
                  <button type="button" onClick={() => setNewIncome({ ...newIncome, alcance: 'habitacion' })}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${newIncome.alcance === 'habitacion' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid="income-alcance-habitacion">
                    Una habitación
                  </button>
                </div>
              </div>
              {newIncome.alcance === 'habitacion' && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Habitación / Apartamento *</Label>
                  <Select value={newIncome.room_id} onValueChange={v => setNewIncome({ ...newIncome, room_id: v })}>
                    <SelectTrigger data-testid="income-room-select"><SelectValue placeholder="Seleccionar habitación..." /></SelectTrigger>
                    <SelectContent>
                      {rooms.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nombre} {r.tipo ? `(${r.tipo})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-sm">Motivo *</Label>
                <Input placeholder="Ej: Daño de sábana, hora extra, penalidad" value={newIncome.motivo} onChange={e => setNewIncome({ ...newIncome, motivo: e.target.value })} data-testid="income-motivo" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Monto *</Label>
                  <Input type="number" min="0" placeholder="0" value={newIncome.monto} onChange={e => setNewIncome({ ...newIncome, monto: e.target.value })} data-testid="income-monto" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Fecha</Label>
                  <Input type="date" value={newIncome.fecha} onChange={e => setNewIncome({ ...newIncome, fecha: e.target.value })} data-testid="income-fecha" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Categoría</Label>
                <Select value={newIncome.categoria} onValueChange={v => setNewIncome({ ...newIncome, categoria: v })}>
                  <SelectTrigger data-testid="income-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dano">Daño / Reparación</SelectItem>
                    <SelectItem value="penalidad">Penalidad</SelectItem>
                    <SelectItem value="hora_extra">Hora Extra</SelectItem>
                    <SelectItem value="producto">Producto / Servicio</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIncomeModal(false)}>Cancelar</Button>
              <Button onClick={addAdditionalIncome} className="bg-green-600 hover:bg-green-700" disabled={!newIncome.motivo || !newIncome.monto || (newIncome.alcance === 'habitacion' && !newIncome.room_id)} data-testid="btn-save-additional-income">
                Registrar Ingreso
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
